import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/Combobox";
import { StatusBadge } from "@/components/StatusBadge";
import { ShiftDrawer } from "@/components/ShiftDrawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtTime, fmtDate } from "@/lib/format";
import { fmtLog } from "@/lib/tz";
import { Loader2, Briefcase, UserPlus, MapPin, Eye, Link2, AlertTriangle, Megaphone } from "lucide-react";
import { JobBlastDialog } from "@/components/JobBlastDialog";
import { NotifyCheckboxes, toastNotifications } from "@/components/shift/NotifyCheckboxes";
import dayjs from "dayjs";
import { toast } from "sonner";

export default function JobBoard() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [services, setServices] = useState([]);
  const [itemGroups, setItemGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [distances, setDistances] = useState({});
  const [clashes, setClashes] = useState({});
  const [unavail, setUnavail] = useState({});
  const [removing, setRemoving] = useState(null);   // {shift, worker, name}
  const [removeReason, setRemoveReason] = useState("");
  const [apply, setApply] = useState(null);
  const [applyForm, setApplyForm] = useState({ worker: null, note: "" });
  const [occupied, setOccupied] = useState(null); // { name, worker, worker_name, current_workers }
  const [drawer, setDrawer] = useState({ open: false, name: null });
  const [blast, setBlast] = useState(null);   // shift name being blasted
  // Optional "shift covered" emails on approve; both off until ticked.
  const [notify, setNotify] = useState({ staff: false, participant: false });

  // Only the first load shows the spinner: refreshing after a save or approval
  // keeps the list on screen, so you stay where you were on the page.
  const load = () => {
    api.jobBoard().then(setRows).catch(() => toast.error("Load failed")).finally(() => setLoading(false));
  };
  useEffect(() => {
    Promise.all([api.staff(), api.participants(), api.serviceItems(), api.itemGroups()])
      .then(([s, p, sv, ig]) => { setStaff(s); setParticipants(p); setServices(sv); setItemGroups(ig); })
      .catch(() => {});
    load();
  }, []); // eslint-disable-line

  const openDetail = async (name) => {
    const { shift } = await api.shift(name);
    if (detail?.name !== name) setNotify({ staff: false, participant: false });
    setDetail(shift);
    setDistances({});
    api.applicantDistances(name).then(setDistances).catch(() => {});
    api.applicantClashes(name).then(setClashes).catch(() => setClashes({}));
    setUnavail({});
    api.applicantUnavailability(name).then(setUnavail).catch(() => setUnavail({}));
  };

  const doApply = async () => {
    if (!applyForm.worker) return toast.error("Select a worker");
    try {
      await api.apply(apply.name, applyForm.worker, applyForm.note);
      toast.success("Application submitted");
      setApply(null); setApplyForm({ worker: null, note: "" });
      openDetail(apply.name); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
  };

  const doApprove = async (name, worker, mode) => {
    if (!mode && (unavail[worker] || []).length > 0 &&
        !window.confirm("This worker's general availability clashes with this shift. Assign them anyway?")) return;
    try {
      const res = await api.approveApp(name, worker, mode, notify);
      if (res?.override_pending) {
        toast.warning(`Assigned, but held for a Rostering Manager: ${res.override_pending_reason || "the shift has a hard block"}. `
          + "It's under Problem unapproved on the roster; the worker isn't told until it's overridden.", { duration: 10000 });
      } else {
        toast.success(mode === "add" ? "Approved — double (2:1) set up" : "Approved & worker assigned");
      }
      toastNotifications(res?.notifications);
      setDetail(null); setOccupied(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "occupied") {
        const wname = staff.find((s) => s.name === worker)?.display_name || worker;
        setOccupied({ name, worker, worker_name: wname, current_workers: d.current_workers || [] });
        return;
      }
      toast.error(d || "Approve failed");
    }
  };

  const wOpts = staff.filter((s) => s.status === "Active").map((s) => ({ value: s.name, label: s.display_name || s.name, hint: s.primary_role }));

  return (
    <div className="p-5">
      <h1 className="text-xl font-bold text-slate-900 tracking-tight">Job Board</h1>
      <p className="text-xs text-slate-500 mt-0.5">Open shifts workers can apply for. Workers cannot self-accept — office staff approve applications here. Audience (gender / preferred / compatible) is set when posting from the roster.</p>

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> :
        rows.length === 0 ? <div className="mt-10 text-center text-sm text-slate-400"><Briefcase className="h-8 w-8 mx-auto mb-2 opacity-40" />No shifts open on the job board.</div> : (() => {
          // Group linked shifts together for visual clarity.
          const grouped = [];
          const seen = new Set();
          const byGroup = {};
          for (const s of rows) { if (s.link_group_id) (byGroup[s.link_group_id] ||= []).push(s); }
          for (const s of rows) {
            if (seen.has(s.name)) continue;
            if (s.link_group_id && byGroup[s.link_group_id]?.length > 1) {
              const legs = byGroup[s.link_group_id].sort((a, b) => (a.link_seq || 0) - (b.link_seq || 0));
              legs.forEach((l) => seen.add(l.name));
              grouped.push({ type: "linked", legs });
            } else { seen.add(s.name); grouped.push({ type: "single", shift: s }); }
          }
          const renderCard = (s) => (
            <div key={s.name} className="bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md transition-shadow" data-testid={`job-card-${s.name}`}>
                <div className="flex items-center justify-between mb-2">
                  <StatusBadge status={s.status} small worker={s.support_worker} />
                  <span className="text-[10px] font-mono text-slate-400">{s.name}</span>
                </div>
                <div className="text-sm font-semibold text-slate-800">{s.participant_name || s.participant}</div>
                <div className="text-xs text-slate-500">{s.service_item}</div>
                {!!s.link_group_id && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5" data-testid={`job-linked-${s.name}`}>
                    <Link2 className="h-2.5 w-2.5" /> Linked package · leg {s.link_seq}
                  </div>
                )}
                <div className="mt-2 space-y-0.5 text-[11px] text-slate-600">
                  <div>{fmtDate(s.shift_date)} · {fmtTime(s.start_time)}–{fmtTime(s.end_time)}</div>
                  {s.location && <div className="flex items-center gap-1 text-slate-400"><MapPin className="h-3 w-3" />{s.location}</div>}
                  {s.job_board_reason && <div className="flex items-start gap-1 text-amber-600" data-testid={`job-reason-${s.name}`}><Briefcase className="h-3 w-3 mt-0.5 shrink-0" />Posted while staffed: {s.job_board_reason}</div>}
                </div>
                {s.clashing_applicants > 0 && (
                  <div className="mt-2 flex items-start gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-semibold text-red-700"
                    data-testid={`job-clash-count-${s.name}`}>
                    <AlertTriangle className="h-3 w-3 mt-px shrink-0" />
                    {s.clashing_applicants} of {s.open_application_count} applicant{s.open_application_count === 1 ? "" : "s"} already rostered at that time
                  </div>
                )}
                {s.unavailable_applicants > 0 && (
                  <div className="mt-2 flex items-start gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-semibold text-red-700"
                    data-testid={`job-unavail-count-${s.name}`}>
                    <AlertTriangle className="h-3 w-3 mt-px shrink-0" />
                    {s.unavailable_applicants} of {s.open_application_count} applicant{s.open_application_count === 1 ? "" : "s"} generally unavailable at that time
                  </div>
                )}
                {!!(s.jb_target_gender || s.jb_preferred_only) && (
                  <div className="flex flex-wrap gap-1 mt-2" data-testid={`job-audience-${s.name}`}>
                    {s.jb_target_gender && <span className="text-[9px] font-semibold uppercase tracking-wide bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">{s.jb_target_gender.split(",").join(", ")} only</span>}
                    {s.jb_preferred_only ? <span className="text-[9px] font-semibold uppercase tracking-wide bg-brand-soft text-brand border border-brand/30 rounded px-1.5 py-0.5">Preferred/team only</span> : null}
                  </div>
                )}
                <div className="flex gap-1.5 mt-3 items-center">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-brand-dark" title="View shift details"
                    onClick={() => setDrawer({ open: true, name: s.name })} data-testid={`job-view-shift-${s.name}`}>
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] flex-1" onClick={() => openDetail(s.name)} data-testid={`job-view-${s.name}`}>
                    View applicants <span className="font-bold text-brand" data-testid={`job-count-${s.name}`}>({s.application_count ?? 0})</span>
                  </Button>
                  {can.has("sms.send") && <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-slate-500 hover:text-brand" title="Text eligible workers about this shift"
                    onClick={() => setBlast(s.name)} data-testid={`job-blast-${s.name}`}>
                    <Megaphone className="h-3.5 w-3.5" />
                  </Button>}
                  {can.has("roster.job_board") && <Button size="sm" className="h-7 text-[11px]" style={{ backgroundColor: "#8B5CF6" }} onClick={() => { setApply(s); setApplyForm({ worker: null, note: "" }); }} data-testid={`job-apply-${s.name}`}><UserPlus className="h-3 w-3 mr-1" />Add applicant</Button>}
                </div>
              </div>
            );
          // Under a heading per date, soonest first (the list comes in start-time order).
          const byDate = [];
          for (const g of grouped) {
            const d = String((g.type === "single" ? g.shift : g.legs[0]).shift_date || "").slice(0, 10);
            if (!byDate.length || byDate[byDate.length - 1].date !== d) byDate.push({ date: d, items: [] });
            byDate[byDate.length - 1].items.push(g);
          }
          return byDate.map(({ date, items }) => (
            <section key={date || "undated"} className="mt-5" data-testid={`job-date-${date}`}>
              <h2 className="text-sm font-bold text-slate-800 border-b border-slate-200 pb-1">
                {date ? dayjs(date).format("dddd D MMMM") : "No date"}
                <span className="ml-2 text-[11px] font-normal text-slate-400">{items.length} shift{items.length === 1 ? "" : "s"}</span>
              </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              {items.map((g, gi) =>
                g.type === "single" ? renderCard(g.shift) : (
                  <div key={`linked-${g.legs[0].link_group_id}`} className="sm:col-span-2 lg:col-span-3 rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-2"
                    data-testid={`job-linked-group-${g.legs[0].link_group_id}`}>
                    <div className="flex items-center gap-1.5 mb-2 px-1">
                      <Link2 className="h-3.5 w-3.5 text-indigo-600" />
                      <span className="text-xs font-semibold text-indigo-800">Linked Package · {g.legs.length} legs</span>
                      <span className="text-[10px] text-indigo-500 ml-1">{g.legs[0].participant_name || g.legs[0].participant} · {g.legs[0].location || "no location"}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {g.legs.map(renderCard)}
                    </div>
                  </div>
                )
              )}
            </div>
            </section>
          ));
        })()}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent data-testid="job-detail-dialog">
          <DialogHeader><DialogTitle className="text-base">{detail?.title || detail?.name} — applicants</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(detail?.applications || []).length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No applications yet.</div>}
            {can.has("roster.job_board") && (detail?.applications || []).some((a) => a.status === "Applied") && (
              <NotifyCheckboxes value={notify} onChange={setNotify} testid="job-approve-notify"
                labels={["Email the worker that they have the shift", "Email the participant that the shift is covered"]}
                hint="Sent when you press Approve & assign, using the email templates in Settings." />
            )}
            {(detail?.applications || []).map((a, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid={`job-applicant-${a.support_worker}`}>
                <div>
                  <div className="text-xs font-medium text-slate-800">{a.worker_name || a.support_worker}</div>
                  <div className="text-[10px] text-slate-400">{a.status} · {fmtLog(a.applied_on)}{a.note ? ` · ${a.note}` : ""}</div>
                  <div className="text-[10px] text-slate-500" data-testid={`job-applicant-distance-${a.support_worker}`}>
                    {distances[a.support_worker] != null ? `${distances[a.support_worker]} km from home` : "Distance unavailable"}
                  </div>
                  {(clashes[a.support_worker] || []).map((c, ci) => (
                    <div key={ci} className="text-[10px] font-semibold text-red-700 flex items-start gap-1"
                      data-testid={`job-applicant-clash-${a.support_worker}`}>
                      <AlertTriangle className="h-3 w-3 mt-px shrink-0" />
                      Already rostered {fmtTime(c.start_time)}–{fmtTime(c.end_time)}
                      {c.participant_name ? ` (${c.participant_name})` : ""}
                      {c.kind === "travel" ? ` — ${c.gap_min} min gap, ~${c.drive_min} min drive`
                        : c.kind === "unknown_distance" ? ` — only a ${c.gap_min} min gap (travel time couldn't be checked)`
                        : " — overlaps this shift"}
                    </div>
                  ))}
                  {a.status === "Applied" && (unavail[a.support_worker] || []).map((h, hi) => (
                    <div key={`u${hi}`} className="text-[10px] font-semibold text-red-700 flex items-start gap-1"
                      data-testid={`job-applicant-unavail-${a.support_worker}`}>
                      <AlertTriangle className="h-3 w-3 mt-px shrink-0" />
                      {h.availability_type === "Maybe Available" ? "Only maybe available" : "Generally unavailable"} {fmtDate(h.date)}{" "}
                      {h.mode === "All Day" ? "(all day)" : `(${String(h.start_time || "").slice(0, 5)}–${String(h.end_time || "").slice(0, 5)})`}
                      {h.title ? ` — ${h.title}` : ""}
                    </div>
                  ))}
                  {a.status === "Removed" && a.removed_reason && (
                    <div className="text-[10px] text-slate-500" data-testid={`job-applicant-removed-${a.support_worker}`}>
                      Removed by {a.removed_by}: “{a.removed_reason}”
                    </div>
                  )}
                </div>
                {can.has("roster.job_board") && a.status === "Applied" && (
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" className="h-7 text-[11px]" style={{ backgroundColor: "var(--brand)" }} onClick={() => doApprove(detail.name, a.support_worker)} data-testid={`job-approve-${a.support_worker}`}>Approve &amp; assign</Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px] text-slate-600"
                      onClick={() => { setRemoving({ shift: detail.name, worker: a.support_worker, name: a.worker_name || a.support_worker }); setRemoveReason(""); }}
                      data-testid={`job-remove-${a.support_worker}`}>Remove</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove application dialog */}
      <Dialog open={!!removing} onOpenChange={(v) => !v && setRemoving(null)}>
        <DialogContent data-testid="job-remove-dialog">
          <DialogHeader><DialogTitle className="text-base">Remove application</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-600 -mt-2">
            Removing <b>{removing?.name}</b>'s application. They'll see it in their app marked as
            removed by the office with your reason, and it's recorded on the shift's audit trail.
          </p>
          <Input value={removeReason} onChange={(e) => setRemoveReason(e.target.value)} className="h-9 text-sm"
            placeholder="Reason (required) — e.g. already rostered elsewhere" data-testid="job-remove-reason" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRemoving(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "var(--brand)" }} data-testid="job-remove-confirm"
              disabled={!removeReason.trim()}
              onClick={async () => {
                try {
                  await api.removeApplication(removing.shift, removing.worker, removeReason.trim());
                  toast.success("Application removed");
                  setRemoving(null);
                  openDetail(removing.shift);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
              }}>Remove application</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply dialog */}
      <Dialog open={!!apply} onOpenChange={(v) => !v && setApply(null)}>
        <DialogContent data-testid="job-apply-dialog">
          <DialogHeader><DialogTitle className="text-base">Add applicant (worker application)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-[11px] font-medium text-slate-600">Support worker</label><div className="mt-1"><Combobox testid="apply-worker" options={wOpts} value={applyForm.worker} onChange={(v) => setApplyForm((f) => ({ ...f, worker: v }))} placeholder="Select worker" /></div></div>
            <div><label className="text-[11px] font-medium text-slate-600">Note</label><Input value={applyForm.note} onChange={(e) => setApplyForm((f) => ({ ...f, note: e.target.value }))} className="h-9 text-xs mt-1" placeholder="Optional note" data-testid="apply-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setApply(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#8B5CF6" }} onClick={doApply} data-testid="apply-confirm">Submit application</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Occupied-shift decision dialog */}
      <Dialog open={!!occupied} onOpenChange={(v) => !v && setOccupied(null)}>
        <DialogContent data-testid="job-occupied-dialog">
          <DialogHeader><DialogTitle className="text-base">Shift already has a worker</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-600 -mt-2">
            This shift is already assigned to <b>{(occupied?.current_workers || []).map((w) => w.worker_name || w.support_worker).join(", ")}</b>.
            How should <b>{occupied?.worker_name}</b> be added?
          </p>
          <div className="space-y-2 mt-1">
            <Button className="w-full justify-start h-auto py-2.5 text-left" variant="outline" data-testid="job-occupied-replace"
              onClick={() => doApprove(occupied.name, occupied.worker, "replace")}>
              <div><div className="text-xs font-semibold text-slate-800">Replace the current worker</div>
                <div className="text-[10px] text-slate-500">Removes the existing worker and assigns {occupied?.worker_name}.</div></div>
            </Button>
            <Button className="w-full justify-start h-auto py-2.5 text-left text-white" data-testid="job-occupied-double"
              style={{ backgroundColor: "var(--brand)" }}
              onClick={() => doApprove(occupied.name, occupied.worker, "add")}>
              <div><div className="text-xs font-semibold">Keep both — double (2:1) ratio</div>
                <div className="text-[10px] opacity-80">Adds {occupied?.worker_name} alongside the current worker and sets a 2:1 ratio.</div></div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOccupied(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift detail side card (same as the roster board) */}
      <JobBlastDialog shift={blast} onOpenChange={(v) => !v && setBlast(null)} />

      <ShiftDrawer
        open={drawer.open}
        onOpenChange={(v) => setDrawer((d) => ({ ...d, open: v }))}
        shiftName={drawer.name}
        participants={participants}
        staff={staff}
        services={services}
        itemGroups={itemGroups}
        onSaved={load}
        onOpenShift={(name) => setDrawer({ open: true, name })}
      />
    </div>
  );
}
